import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space,
  Spin, message, Table, Modal, Form, Input,
  DatePicker, Select, Tabs, Alert, Empty, Badge,
  Tooltip, Divider, Upload, Image, Avatar, Popconfirm
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  BankOutlined,
  FileTextOutlined,
  SettingOutlined,
  EditOutlined,
  SaveOutlined,
  UploadOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BellOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  LogoutOutlined,
  HomeOutlined,
  ApartmentOutlined,
  TrophyOutlined,
  QrcodeOutlined,
  EyeOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../api';
import StaffLayout from './StaffLayout';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export default function StaffMyProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');

  // Profile State (matching ProfileScreen.js)
  const [profile, setProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm] = Form.useForm();
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // General Info State (matching GeneralInfoScreen.js)
  const [generalInfo, setGeneralInfo] = useState(null);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [generalForm] = Form.useForm();

  // Bank Details State (matching BankDetailsScreen.js)
  const [bankDetails, setBankDetails] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankForm] = Form.useForm();

  // Documents State (matching MyDocumentsScreen.js)
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadingDocId, setUploadingDocId] = useState(null);

  // Notifications Modal (matching ProfileScreen.js leaves notifications)
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLeaves, setNotifLeaves] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);

  // QR Status (matching ProfileScreen.js lines 62-85)
  const [qrPunchRestricted, setQrPunchRestricted] = useState(false);
  const [isQrZoneAssigned, setIsQrZoneAssigned] = useState(true);
  const [punchedInAt, setPunchedInAt] = useState(null);
  const [punchedOutAt, setPunchedOutAt] = useState(null);

  // Self Appraisal State (matching SelfAppraisalScreen.js)
  const [appraisals, setAppraisals] = useState([]);
  const [appraisalLoading, setAppraisalLoading] = useState(false);

  // Account Settings (matching AccountSettingsScreen.js)
  const [accountEmail, setAccountEmail] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);

  // Photo URL builder
  const photoUrl = useMemo(() => {
    const p = profile?.photoUrl;
    if (!p) return null;
    if (String(p).startsWith('http')) return String(p);
    return `${API_BASE_URL}${p}`;
  }, [profile?.photoUrl]);

  // Load Profile (matching ProfileScreen.js getMyProfile)
  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/me/profile');
      if (res.data?.success && res.data.profile) {
        const p = res.data.profile;
        setProfile(p);
        setAccountEmail(p.email || '');
        profileForm.setFieldsValue({
          name: p.name || '',
          email: p.email || '',
          designation: p.designation || '',
          department: p.department || ''
        });

        // Sync local storage user
        const cur = localStorage.getItem('user');
        const parsed = cur ? JSON.parse(cur) : {};
        const updatedUser = {
          ...parsed,
          name: p.name || parsed.name,
          phone: p.phone || parsed.phone,
          role: p.role || parsed.role,
          staffId: p.staffId || parsed.staffId,
          designation: p.designation || parsed.designation,
          department: p.department || parsed.department,
          photoUrl: p.photoUrl || parsed.photoUrl
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        window.dispatchEvent(new CustomEvent('profile-updated', { detail: updatedUser }));
      }
    } catch (err) {
      console.error('Load profile error:', err);
      message.error('Unable to load profile.');
    } finally {
      setLoading(false);
    }
  }, [profileForm]);

  // Load General Info (matching GeneralInfoScreen.js getMyGeneralInfo)
  const loadGeneralInfo = useCallback(async () => {
    try {
      setGeneralLoading(true);
      const res = await api.get('/me/general');
      if (res.data?.success && res.data.general) {
        const g = res.data.general;
        setGeneralInfo(g);
        generalForm.setFieldsValue({
          fullName: g.fullName || '',
          dob: g.dob ? dayjs(g.dob) : null,
          gender: g.gender || undefined,
          maritalStatus: g.maritalStatus || undefined,
          bloodGroup: g.bloodGroup || '',
          nationality: g.nationality || 'Indian',
          personalMobile: g.personalMobile || '',
          emergencyContactName: g.emergencyContactName || '',
          emergencyContactNumber: g.emergencyContactNumber || '',
          currentAddress: g.currentAddress || '',
          permanentAddress: g.permanentAddress || '',
          designation: g.designation || '',
          department: g.department || '',
          employeeType: g.employeeType || 'regular',
          dateOfJoining: g.dateOfJoining ? dayjs(g.dateOfJoining) : null,
          workLocation: g.workLocation || '',
          reportingManager: g.reportingManager || '',
          shiftTiming: g.shiftTiming || ''
        });
      }
    } catch (err) {
      console.error('Load general info error:', err);
    } finally {
      setGeneralLoading(false);
    }
  }, [generalForm]);

  // Load Bank Details (matching BankDetailsScreen.js getMyBankDetails)
  const loadBankDetails = useCallback(async () => {
    try {
      setBankLoading(true);
      const res = await api.get('/me/bank');
      if (res.data?.success && res.data.bank) {
        const b = res.data.bank;
        setBankDetails(b);
        bankForm.setFieldsValue({
          bankAccountHolderName: b.bankAccountHolderName || '',
          bankAccountNumber: b.bankAccountNumber || '',
          bankIfsc: b.bankIfsc || '',
          bankName: b.bankName || '',
          bankBranch: b.bankBranch || '',
          upiId: b.upiId || ''
        });
      }
    } catch (err) {
      console.error('Load bank details error:', err);
    } finally {
      setBankLoading(false);
    }
  }, [bankForm]);

  // Load Required Documents (matching MyDocumentsScreen.js listRequiredDocuments)
  const loadDocuments = useCallback(async () => {
    try {
      setDocsLoading(true);
      const res = await api.get('/documents/required');
      if (res.data?.success && Array.isArray(res.data.documents)) {
        setDocuments(res.data.documents);
      }
    } catch (err) {
      console.error('Load documents error:', err);
    } finally {
      setDocsLoading(false);
    }
  }, []);

  // Load Leave Notifications (matching ProfileScreen.js lines 92-111)
  const loadNotifications = useCallback(async () => {
    try {
      const [apRes, rjRes] = await Promise.all([
        api.get('/leaves/my?status=APPROVED').catch(() => ({ data: { leaves: [] } })),
        api.get('/leaves/my?status=REJECTED').catch(() => ({ data: { leaves: [] } }))
      ]);

      const approved = (apRes.data?.leaves || []).map((x) => ({ ...x, _notifStatus: 'APPROVED' }));
      const rejected = (rjRes.data?.leaves || []).map((x) => ({ ...x, _notifStatus: 'REJECTED' }));
      const all = [...approved, ...rejected].sort((a, b) =>
        new Date(b.reviewedAt || b.updatedAt || b.createdAt).getTime() - new Date(a.reviewedAt || a.updatedAt || a.createdAt).getTime()
      );
      setNotifLeaves(all);
      setUnseenCount(all.length);
    } catch (_) {}
  }, []);

  // Load Attendance Status (matching ProfileScreen.js fetchQrStatus)
  const loadAttendanceStatus = useCallback(async () => {
    try {
      const res = await api.get('/attendance/status').catch(() => null);
      if (res?.data?.success && res.data.status) {
        setQrPunchRestricted(!!res.data.status.qrPunchRestricted);
        setIsQrZoneAssigned(res.data.status.isQrZoneAssigned !== false);
        setPunchedInAt(res.data.status.punchedInAt || null);
        setPunchedOutAt(res.data.status.punchedOutAt || null);
      }
    } catch (_) {}
  }, []);

  // Load Self Appraisals
  const loadSelfAppraisals = useCallback(async () => {
    try {
      setAppraisalLoading(true);
      const res = await api.get('/admin/pms/self-appraisals/my').catch(() => null);
      if (res?.data?.success && Array.isArray(res.data.appraisals)) {
        setAppraisals(res.data.appraisals);
      }
    } catch (_) {} finally {
      setAppraisalLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    loadGeneralInfo();
    loadBankDetails();
    loadDocuments();
    loadNotifications();
    loadAttendanceStatus();
    loadSelfAppraisals();
  }, [loadProfile, loadGeneralInfo, loadBankDetails, loadDocuments, loadNotifications, loadAttendanceStatus, loadSelfAppraisals]);

  // Save Profile (Name, Email, Designation, Department - matching updateMyProfile)
  const handleSaveProfile = async (values) => {
    try {
      setSavingProfile(true);
      const res = await api.put('/me/profile', {
        name: values.name?.trim(),
        email: values.email?.trim(),
        designation: values.designation?.trim(),
        department: values.department?.trim()
      });

      if (res.data?.success) {
        message.success('Profile updated successfully.');
        setEditingProfile(false);
        await loadProfile();
      } else {
        message.error(res.data?.message || 'Unable to update profile.');
      }
    } catch (err) {
      console.error('Update profile error:', err);
      message.error(err.response?.data?.message || 'Unable to update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  // Upload Profile Photo (matching uploadMyProfilePhoto)
  const handlePhotoUpload = async (file) => {
    if (file.size > 2 * 1024 * 1024) {
      message.error('Image size exceeds the limit of 2MB!');
      return false;
    }
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      message.error('Only Images (JPG, PNG) are allowed!');
      return false;
    }

    try {
      setUploadingPhoto(true);
      const form = new FormData();
      form.append('photo', file);

      const res = await api.post('/me/profile/photo', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        message.success('Profile photo updated successfully.');
        await loadProfile();
      } else {
        message.error(res.data?.message || 'Unable to upload photo.');
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      message.error(err.response?.data?.message || 'Unable to upload photo.');
    } finally {
      setUploadingPhoto(false);
    }
    return false;
  };

  // Save General Info (matching updateMyGeneralInfo)
  const handleSaveGeneralInfo = async (values) => {
    try {
      setSavingGeneral(true);
      const payload = {
        fullName: values.fullName?.trim(),
        dob: values.dob ? values.dob.format('YYYY-MM-DD') : null,
        gender: values.gender,
        maritalStatus: values.maritalStatus,
        bloodGroup: values.bloodGroup?.trim(),
        nationality: values.nationality?.trim(),
        personalMobile: values.personalMobile?.trim(),
        emergencyContactName: values.emergencyContactName?.trim(),
        emergencyContactNumber: values.emergencyContactNumber?.trim(),
        currentAddress: values.currentAddress?.trim(),
        permanentAddress: values.permanentAddress?.trim()
      };

      const res = await api.put('/me/general', payload);
      if (res.data?.success) {
        message.success('General information saved successfully.');
        await loadGeneralInfo();
      } else {
        message.error(res.data?.message || 'Unable to save general information.');
      }
    } catch (err) {
      console.error('Save general info error:', err);
      message.error(err.response?.data?.message || 'Unable to save general info.');
    } finally {
      setSavingGeneral(false);
    }
  };

  // Save Bank Details (matching updateMyBankDetails)
  const handleSaveBankDetails = async (values) => {
    try {
      setSavingBank(true);
      const res = await api.put('/me/bank', {
        bankAccountHolderName: values.bankAccountHolderName?.trim(),
        bankAccountNumber: values.bankAccountNumber?.trim(),
        bankIfsc: values.bankIfsc?.trim(),
        bankName: values.bankName?.trim(),
        bankBranch: values.bankBranch?.trim(),
        upiId: values.upiId?.trim()
      });

      if (res.data?.success) {
        message.success('Bank details saved successfully.');
        await loadBankDetails();
      } else {
        message.error(res.data?.message || 'Unable to save bank details.');
      }
    } catch (err) {
      console.error('Save bank details error:', err);
      message.error(err.response?.data?.message || 'Unable to save bank details.');
    } finally {
      setSavingBank(false);
    }
  };

  // Upload Staff Document (matching uploadStaffDocument)
  const handleDocumentUpload = async (docTypeId, file) => {
    if (file.size > 10 * 1024 * 1024) {
      message.error('File size exceeds the limit of 10MB!');
      return false;
    }

    try {
      setUploadingDocId(docTypeId);
      const form = new FormData();
      form.append('file', file);

      const res = await api.post(`/documents/${docTypeId}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        message.success('Document uploaded successfully.');
        await loadDocuments();
      } else {
        message.error(res.data?.message || 'Failed to upload document.');
      }
    } catch (err) {
      console.error('Upload document error:', err);
      message.error(err.response?.data?.message || 'Failed to upload document.');
    } finally {
      setUploadingDocId(null);
    }
    return false;
  };

  // Save Account Settings Handler (matching AccountSettingsScreen.js onSave)
  const handleSaveAccountSettings = async () => {
    try {
      setSavingAccount(true);
      const res = await api.put('/me/profile', { email: accountEmail });
      if (res.data?.success) {
        message.success('Account settings saved successfully.');
        await loadProfile();
      } else {
        message.error(res.data?.message || 'Unable to save account settings.');
      }
    } catch (err) {
      console.error('Save account settings error:', err);
      message.error(err.response?.data?.message || 'Unable to save account settings.');
    } finally {
      setSavingAccount(false);
    }
  };

  // Logout Handler (matching onLogout)
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    navigate('/');
  };

  return (
    <StaffLayout>
      {/* 1. TOP HEADER & QUICK ACTIONS */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 22,
            boxShadow: '0 8px 16px -4px rgba(16, 185, 129, 0.35)'
          }}>
            <UserOutlined />
          </div>
          <div>
            <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
              My Profile & Settings
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Personal information, bank details, documents, and account settings — identical to Mobile App
            </Text>
          </div>
        </div>

        {/* Header Right Actions */}
        <Space size="middle" wrap>
          {/* Notifications button commented out (replaced by central top navbar bell) */}
          {/* <Badge count={unseenCount} overflowCount={9}>
            <Button
              type="default"
              icon={<BellOutlined style={{ fontSize: 16, color: '#4B5563' }} />}
              onClick={() => setNotifOpen(true)}
              style={{ borderRadius: 8, height: 38 }}
            >
              Notifications
            </Button>
          </Badge> */}

          <Button
            type="default"
            icon={<SyncOutlined spin={loading} />}
            onClick={() => {
              loadProfile();
              loadGeneralInfo();
              loadBankDetails();
              loadDocuments();
            }}
            style={{ borderRadius: 8, height: 38 }}
          >
            Refresh
          </Button>

          <Button
            type="primary"
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{ borderRadius: 8, height: 38, fontWeight: 600 }}
          >
            Log out
          </Button>
        </Space>
      </div>

      {/* 2. PROFILE HERO CARD (Matching ProfileScreen.js lines 224-306) */}
      <Card
        style={{
          borderRadius: 14,
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          marginBottom: 24,
          background: '#FFFFFF'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <Row gutter={[24, 24]} align="middle">
          {/* Avatar & Photo Upload column */}
          <Col xs={24} sm={8} md={6} lg={5} style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                size={96}
                src={photoUrl}
                icon={!photoUrl ? <UserOutlined /> : null}
                style={{
                  backgroundColor: '#059669',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '3px solid #FFFFFF'
                }}
              />
              <Upload
                beforeUpload={handlePhotoUpload}
                showUploadList={false}
                accept="image/png,image/jpeg,image/jpg"
              >
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    background: '#1D4ED8',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                    border: '2px solid #FFFFFF'
                  }}
                  title="Upload profile photo"
                >
                  {uploadingPhoto ? <SyncOutlined spin /> : <CameraOutlined />}
                </div>
              </Upload>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>
              Max 2 MB, JPG/PNG format
            </div>
          </Col>

          {/* Profile details column */}
          <Col xs={24} sm={16} md={18} lg={19}>
            {!editingProfile ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#0F172A' }}>
                      {profile?.name || 'Staff User'}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748B', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{profile?.designation || 'Staff Member'}</span>
                      {profile?.department && (
                        <>
                          <span>•</span>
                          <span>{profile.department}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <Button
                    type="primary"
                    ghost
                    icon={<EditOutlined />}
                    onClick={() => setEditingProfile(true)}
                    style={{ borderRadius: 8, fontWeight: 600 }}
                  >
                    Edit Profile
                  </Button>
                </div>

                <Divider style={{ margin: '14px 0' }} />

                <Row gutter={[16, 12]}>
                  <Col xs={24} sm={12} md={6}>
                    <div style={{ color: '#64748B', fontSize: 12 }}>Phone number</div>
                    <div style={{ fontWeight: 600, color: '#1E293B', fontSize: 13, marginTop: 2 }}>
                      {profile?.phone ? `+91 ${profile.phone}` : '--'}
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <div style={{ color: '#64748B', fontSize: 12 }}>Email address</div>
                    <div style={{ fontWeight: 600, color: '#1E293B', fontSize: 13, marginTop: 2 }}>
                      {profile?.email || '--'}
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <div style={{ color: '#64748B', fontSize: 12 }}>Staff ID</div>
                    <div style={{ fontWeight: 600, color: '#1E293B', fontSize: 13, marginTop: 2 }}>
                      {profile?.staffId ? String(profile.staffId).toUpperCase() : '--'}
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <div style={{ color: '#64748B', fontSize: 12 }}>Role</div>
                    <div style={{ marginTop: 2 }}>
                      <Tag color="green" style={{ borderRadius: 6, fontWeight: 600, textTransform: 'uppercase' }}>
                        {profile?.role || 'STAFF'}
                      </Tag>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <Form
                form={profileForm}
                layout="vertical"
                onFinish={handleSaveProfile}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 600, fontSize: 12 }}>Name</span>}
                      name="name"
                      rules={[{ required: true, message: 'Please enter name' }]}
                    >
                      <Input placeholder="Full Name" style={{ borderRadius: 6, height: 38 }} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 600, fontSize: 12 }}>Email</span>}
                      name="email"
                    >
                      <Input placeholder="Email Address" style={{ borderRadius: 6, height: 38 }} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 600, fontSize: 12 }}>Designation</span>}
                      name="designation"
                    >
                      <Input placeholder="Designation" style={{ borderRadius: 6, height: 38 }} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 600, fontSize: 12 }}>Department</span>}
                      name="department"
                    >
                      <Input placeholder="Department" style={{ borderRadius: 6, height: 38 }} />
                    </Form.Item>
                  </Col>
                </Row>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                  <Button onClick={() => setEditingProfile(false)} style={{ borderRadius: 6 }}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={savingProfile} style={{ borderRadius: 6, fontWeight: 600 }}>
                    Save Changes
                  </Button>
                </div>
              </Form>
            )}
          </Col>
        </Row>
      </Card>

      {/* 3. MAIN PROFILE SECTIONS & TABS (Matching APK Settings & List Sections) */}
      <Card
        style={{
          borderRadius: 14,
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="middle"
          items={[
            // ==========================================
            // TAB 1: GENERAL INFO (GeneralInfoScreen.js)
            // ==========================================
            {
              key: 'general',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <UserOutlined /> General Information
                </span>
              ),
              children: (
                <div>
                  <Form
                    form={generalForm}
                    layout="vertical"
                    onFinish={handleSaveGeneralInfo}
                  >
                    {/* SECTION 1: Personal Details */}
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
                        Personal Details
                      </div>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item label="Full Name" name="fullName">
                            <Input placeholder="Full Legal Name" style={{ borderRadius: 6, height: 38 }} />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                          <Form.Item label="Date of Birth" name="dob">
                            <DatePicker format="DD-MM-YYYY" style={{ width: '100%', borderRadius: 6, height: 38 }} />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                          <Form.Item label="Gender" name="gender">
                            <Select placeholder="Select gender" style={{ height: 38, borderRadius: 6 }}>
                              <Option value="Male">Male</Option>
                              <Option value="Female">Female</Option>
                              <Option value="Other">Other</Option>
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                          <Form.Item label="Marital Status" name="maritalStatus">
                            <Select placeholder="Select status" style={{ height: 38, borderRadius: 6 }}>
                              <Option value="Married">Married</Option>
                              <Option value="Unmarried">Unmarried</Option>
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                          <Form.Item label="Blood Group" name="bloodGroup">
                            <Input placeholder="e.g. O+, B+, A+" style={{ borderRadius: 6, height: 38 }} />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                          <Form.Item label="Nationality" name="nationality">
                            <Input placeholder="e.g. Indian" style={{ borderRadius: 6, height: 38 }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>

                    <Divider style={{ margin: '14px 0 20px 0' }} />

                    {/* SECTION 2: Contact & Emergency */}
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
                        Contact & Emergency Details
                      </div>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            label="Personal Mobile"
                            name="personalMobile"
                            rules={[
                              { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit number' }
                            ]}
                          >
                            <Input placeholder="10-digit mobile number" maxLength={10} style={{ borderRadius: 6, height: 38 }} />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                          <Form.Item label="Emergency Contact Name" name="emergencyContactName">
                            <Input placeholder="Relative or Guardian name" style={{ borderRadius: 6, height: 38 }} />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            label="Emergency Contact Number"
                            name="emergencyContactNumber"
                            rules={[
                              { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit number' }
                            ]}
                          >
                            <Input placeholder="10-digit emergency phone" maxLength={10} style={{ borderRadius: 6, height: 38 }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>

                    <Divider style={{ margin: '14px 0 20px 0' }} />

                    {/* SECTION 3: Residential Addresses */}
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
                        Residential Addresses
                      </div>
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item label="Current Address" name="currentAddress">
                            <TextArea rows={2} placeholder="Current residential address..." style={{ borderRadius: 6 }} />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item label="Permanent Address" name="permanentAddress">
                            <TextArea rows={2} placeholder="Permanent home address..." style={{ borderRadius: 6 }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>

                    <Divider style={{ margin: '14px 0 20px 0' }} />

                    {/* SECTION 4: Employment Details (Read-only / Organization mapped) */}
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>
                        Employment Details (Organization Record)
                      </div>
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                          <div style={{ color: '#64748B', fontSize: 12 }}>Designation</div>
                          <div style={{ fontWeight: 600, color: '#0F172A', marginTop: 2 }}>{generalInfo?.designation || '--'}</div>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <div style={{ color: '#64748B', fontSize: 12 }}>Department</div>
                          <div style={{ fontWeight: 600, color: '#0F172A', marginTop: 2 }}>{generalInfo?.department || '--'}</div>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <div style={{ color: '#64748B', fontSize: 12 }}>Date of Joining</div>
                          <div style={{ fontWeight: 600, color: '#0F172A', marginTop: 2 }}>
                            {generalInfo?.dateOfJoining ? dayjs(generalInfo.dateOfJoining).format('DD MMM YYYY') : '--'}
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <div style={{ color: '#64748B', fontSize: 12 }}>Employee Type</div>
                          <div style={{ fontWeight: 600, color: '#0F172A', marginTop: 2, textTransform: 'capitalize' }}>
                            {generalInfo?.employeeType || 'Regular'}
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={savingGeneral}
                        icon={<SaveOutlined />}
                        style={{ borderRadius: 6, fontWeight: 600, height: 38, padding: '0 20px' }}
                      >
                        Save General Information
                      </Button>
                    </div>
                  </Form>
                </div>
              )
            },

            // ==========================================
            // TAB 2: BANK DETAILS (BankDetailsScreen.js)
            // ==========================================
            {
              key: 'bank',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <BankOutlined /> Bank Details
                </span>
              ),
              children: (
                <div>
                  <div style={{
                    background: '#F8FAFC',
                    borderRadius: 8,
                    padding: '12px 16px',
                    border: '1px solid #E2E8F0',
                    marginBottom: 20,
                    color: '#475569',
                    fontSize: 13
                  }}>
                    Bank details are utilized for salary disbursal and reimbursement transfers. Please ensure IFSC and Account number are exact.
                  </div>

                  <Form
                    form={bankForm}
                    layout="vertical"
                    onFinish={handleSaveBankDetails}
                  >
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, fontSize: 12 }}>Account Holder Name</span>}
                          name="bankAccountHolderName"
                        >
                          <Input placeholder="As per bank passbook" style={{ borderRadius: 6, height: 38 }} />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, fontSize: 12 }}>Account Number</span>}
                          name="bankAccountNumber"
                        >
                          <Input placeholder="Enter bank account number" style={{ borderRadius: 6, height: 38 }} />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, fontSize: 12 }}>Bank IFSC Code</span>}
                          name="bankIfsc"
                        >
                          <Input placeholder="e.g. SBIN0001234" style={{ borderRadius: 6, height: 38, textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, fontSize: 12 }}>Bank Name</span>}
                          name="bankName"
                        >
                          <Input placeholder="e.g. State Bank of India" style={{ borderRadius: 6, height: 38 }} />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, fontSize: 12 }}>Branch Name</span>}
                          name="bankBranch"
                        >
                          <Input placeholder="e.g. Connaught Place Branch" style={{ borderRadius: 6, height: 38 }} />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, fontSize: 12 }}>UPI ID (Optional)</span>}
                          name="upiId"
                        >
                          <Input placeholder="username@upi" style={{ borderRadius: 6, height: 38 }} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={savingBank}
                        icon={<SaveOutlined />}
                        style={{ borderRadius: 6, fontWeight: 600, height: 38, padding: '0 20px' }}
                      >
                        Save Bank Details
                      </Button>
                    </div>
                  </Form>
                </div>
              )
            },

            // ==========================================
            // TAB 3: MY DOCUMENTS (MyDocumentsScreen.js)
            // ==========================================
            {
              key: 'documents',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <FileTextOutlined /> My Documents ({documents.length})
                </span>
              ),
              children: (
                <div>
                  <div style={{
                    background: '#F8FAFC',
                    borderRadius: 8,
                    padding: '12px 16px',
                    border: '1px solid #E2E8F0',
                    marginBottom: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 10
                  }}>
                    <div style={{ color: '#475569', fontSize: 13 }}>
                      Official KYC and employment compliance documents required by organization (Max 10MB per file: PDF / JPG / PNG).
                    </div>
                    <Button size="small" icon={<SyncOutlined spin={docsLoading} />} onClick={loadDocuments}>
                      Refresh
                    </Button>
                  </div>

                  {documents.length === 0 ? (
                    <Empty description="No documents required at this time" style={{ padding: '40px 0' }} />
                  ) : (
                    <Table
                      dataSource={documents}
                      rowKey="id"
                      pagination={false}
                      columns={[
                        {
                          title: 'Document Type',
                          key: 'name',
                          render: (_, r) => (
                            <div>
                              <div style={{ fontWeight: 600, color: '#1F2937', fontSize: 14 }}>
                                {r.name || r.key}
                              </div>
                              <div style={{ color: '#64748B', fontSize: 12 }}>
                                {r.required ? (
                                  <Tag color="red" style={{ borderRadius: 4, fontSize: 10, marginTop: 4 }}>Mandatory</Tag>
                                ) : (
                                  <Tag color="default" style={{ borderRadius: 4, fontSize: 10, marginTop: 4 }}>Optional</Tag>
                                )}
                              </div>
                            </div>
                          )
                        },
                        {
                          title: 'Status',
                          key: 'status',
                          width: 140,
                          render: (_, r) => (
                            r.uploaded ? (
                              <Tag color="green" icon={<CheckCircleOutlined />} style={{ borderRadius: 6, fontWeight: 600, padding: '2px 8px' }}>
                                Uploaded
                              </Tag>
                            ) : (
                              <Tag color="orange" icon={<CloseCircleOutlined />} style={{ borderRadius: 6, fontWeight: 600, padding: '2px 8px' }}>
                                Pending
                              </Tag>
                            )
                          )
                        },
                        {
                          title: 'Action',
                          key: 'action',
                          width: 220,
                          render: (_, r) => (
                            <Space size="small">
                              <Upload
                                beforeUpload={(file) => handleDocumentUpload(r.id, file)}
                                showUploadList={false}
                                accept="application/pdf,image/png,image/jpeg,image/jpg"
                              >
                                <Button
                                  size="small"
                                  type={r.uploaded ? 'default' : 'primary'}
                                  icon={<UploadOutlined />}
                                  loading={uploadingDocId === r.id}
                                  style={{ borderRadius: 6, fontSize: 12 }}
                                >
                                  {r.uploaded ? 'Re-upload' : 'Upload File'}
                                </Button>
                              </Upload>

                              {r.fileUrl && (
                                <Button
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={() => window.open(r.fileUrl.startsWith('http') ? r.fileUrl : `${API_BASE_URL}${r.fileUrl}`, '_blank')}
                                  style={{ borderRadius: 6, fontSize: 12 }}
                                >
                                  View
                                </Button>
                              )}
                            </Space>
                          )
                        }
                      ]}
                    />
                  )}
                </div>
              )
            },

            // ==========================================
            // TAB 4: APPRAISALS (SelfAppraisalScreen.js)
            // ==========================================
            {
              key: 'appraisal',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <TrophyOutlined /> Self Appraisal
                </span>
              ),
              children: (
                <div>
                  <div style={{
                    background: '#F8FAFC',
                    borderRadius: 8,
                    padding: '12px 16px',
                    border: '1px solid #E2E8F0',
                    marginBottom: 20,
                    color: '#475569',
                    fontSize: 13
                  }}>
                    PMS performance management self-assessments and appraisal reviews assigned to you.
                  </div>

                  {appraisals.length === 0 ? (
                    <Empty description="No self appraisals active for this cycle" style={{ padding: '40px 0' }} />
                  ) : (
                    <Table
                      dataSource={appraisals}
                      rowKey="id"
                      pagination={false}
                      columns={[
                        {
                          title: 'Appraisal Cycle',
                          dataIndex: 'cycleName',
                          key: 'cycleName',
                          render: (t) => <Text strong>{t || 'Annual Performance Review'}</Text>
                        },
                        {
                          title: 'Status',
                          dataIndex: 'status',
                          key: 'status',
                          render: (s) => (
                            <Tag color={s === 'SUBMITTED' ? 'green' : 'orange'}>
                              {s || 'PENDING'}
                            </Tag>
                          )
                        },
                        {
                          title: 'Due Date',
                          dataIndex: 'dueDate',
                          key: 'dueDate',
                          render: (d) => d ? dayjs(d).format('DD MMM YYYY') : '--'
                        }
                      ]}
                    />
                  )}
                </div>
              )
            },

            // ==========================================
            // TAB 5: ACCOUNT SETTINGS (AccountSettingsScreen.js)
            // ==========================================
            {
              key: 'account',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <SettingOutlined /> Account Settings
                </span>
              ),
              children: (
                <div style={{ maxWidth: 640 }}>
                  <Card
                    title={<span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Account Settings</span>}
                    style={{ borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                    bodyStyle={{ padding: 24 }}
                  >
                    <div style={{ marginBottom: 18 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        Staff ID (Read only)
                      </label>
                      <Input
                        value={profile?.staffId ? String(profile.staffId).toUpperCase() : '-'}
                        disabled
                        style={{
                          borderRadius: 8,
                          height: 42,
                          backgroundColor: '#F9FAFB',
                          color: '#6B7280',
                          fontWeight: 500,
                          border: '1px solid #E5E7EB'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: 18 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        Mobile Number (Read only)
                      </label>
                      <Input
                        value={profile?.phone ? `+91 ${profile.phone}` : '-'}
                        disabled
                        style={{
                          borderRadius: 8,
                          height: 42,
                          backgroundColor: '#F9FAFB',
                          color: '#6B7280',
                          fontWeight: 500,
                          border: '1px solid #E5E7EB'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: 24 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        Email (Editable)
                      </label>
                      <Input
                        value={accountEmail}
                        onChange={(e) => setAccountEmail(e.target.value)}
                        placeholder="Enter email"
                        type="email"
                        style={{
                          borderRadius: 8,
                          height: 42,
                          border: '1px solid #D1D5DB'
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: 12, paddingTop: 16, borderTop: '1px solid #E5E7EB' }}>
                      <Button
                        type="primary"
                        onClick={handleSaveAccountSettings}
                        loading={savingAccount}
                        style={{
                          flex: 1,
                          height: 42,
                          borderRadius: 8,
                          backgroundColor: '#125EC9',
                          borderColor: '#125EC9',
                          fontWeight: 600,
                          fontSize: 14
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        danger
                        type="primary"
                        onClick={handleLogout}
                        style={{
                          flex: 1,
                          height: 42,
                          borderRadius: 8,
                          backgroundColor: '#FF4747',
                          borderColor: '#FF4747',
                          fontWeight: 600,
                          fontSize: 14
                        }}
                      >
                        Logout
                      </Button>
                    </div>
                  </Card>
                </div>
              )
            },

            // ==========================================
            // TAB 6: HELP & SUPPORT (HelpSupportScreen.js)
            // ==========================================
            {
              key: 'help',
              label: (
                <span style={{ fontWeight: 600 }}>
                  <QuestionCircleOutlined /> Help & Support
                </span>
              ),
              children: (
                <div style={{ maxWidth: 640 }}>
                  <Card
                    title={<span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Help & Support</span>}
                    style={{ borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                    bodyStyle={{ padding: 24 }}
                  >
                    <div style={{ marginBottom: 18 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        Support Email (Read only)
                      </label>
                      <Input
                        value="support@thinktechsoftware.in"
                        disabled
                        style={{
                          borderRadius: 8,
                          height: 42,
                          backgroundColor: '#F9FAFB',
                          color: '#374151',
                          border: '1px solid #E5E7EB'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: 18 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                        Support Mobile (Read only)
                      </label>
                      <Input
                        value="+91 99831052038"
                        disabled
                        style={{
                          borderRadius: 8,
                          height: 42,
                          backgroundColor: '#F9FAFB',
                          color: '#374151',
                          border: '1px solid #E5E7EB'
                        }}
                      />
                    </div>

                    <div style={{ color: '#6B7280', fontSize: 13, marginTop: 12 }}>
                      For any issue, contact on above details.
                    </div>
                  </Card>
                </div>
              )
            }
          ]}
        />
      </Card>

      {/* ========================================================================= */}
      {/* 4. MODAL: NOTIFICATIONS (Matching ProfileScreen.js lines 383-417)          */}
      {/* ========================================================================= */}
      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 700 }}>Leave Notifications</span>}
        open={notifOpen}
        onCancel={() => setNotifOpen(false)}
        footer={null}
        width={500}
      >
        <div style={{ marginTop: 10, maxHeight: 420, overflowY: 'auto' }}>
          {notifLeaves.length === 0 ? (
            <Empty description="No leave notifications yet" style={{ padding: '30px 0' }} />
          ) : (
            notifLeaves.map((lv) => (
              <div
                key={String(lv.id)}
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 10,
                  padding: '12px 14px',
                  marginBottom: 10,
                  backgroundColor: lv._notifStatus === 'APPROVED' ? '#F0FDF4' : '#FEF2F2'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: lv._notifStatus === 'APPROVED' ? '#065F46' : '#B91C1C'
                  }}>
                    {lv._notifStatus === 'APPROVED' ? 'Leave Approved' : 'Leave Rejected'}
                  </span>
                  <Tag color={lv._notifStatus === 'APPROVED' ? 'green' : 'red'}>
                    {lv.leaveType || 'LEAVE'}
                  </Tag>
                </div>

                <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
                  Start: {lv.startDate ? dayjs(lv.startDate).format('DD MMM YYYY') : '--'}  •  End: {lv.endDate ? dayjs(lv.endDate).format('DD MMM YYYY') : '--'}
                </div>

                {lv.reviewedAt && (
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                    Reviewed on: {dayjs(lv.reviewedAt).format('DD MMM YYYY, hh:mm A')}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </Modal>
    </StaffLayout>
  );
}
